import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Input, Form, DatePicker, Alert, Spin, Checkbox } from 'antd';
import moment from 'moment';
import { ImageInput, Avatar, PhoneInput } from 'components/elements';
import Logo from 'assets/images/wing-logo.svg';
import {
  useLoggedInStateQuery,
  useUpdateProfileMutation,
  useGetUserByEmailLazyQuery,
  useUpdateReferenceMutation,
  Enum_Userspermissionsuser_Status,
} from 'graphql/types';
import useRegister from 'hooks/useRegister';
import { useHistory, Link } from 'react-router-dom';
import { ResizeImageResult } from 'helpers';
import {
  AuthPage,
  AuthPageCard,
  LoginBody,
  LogoMark,
  LoginHeading,
  LoginSubheading,
  StyledForm,
  SignInButton,
  LoginFooter,
  SignUpLink,
  SignupAvatarRow,
  SignupPictureHint,
  SignupRecordingNotice,
  SignupRiskAcknowledgement,
  SignupRiskAcknowledgementText,
} from '../login/login.styled';
import { birthDayFormat } from 'settings/configs';

interface SignupProps {
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  birthdate: moment.Moment;
  referenceEmail1: string;
  prefix: string;
  phone: string;
  password: string;
  avatar: ResizeImageResult;
  riskAcknowledged: boolean;
}

function AvatarInput({
  onChange,
  value,
  loading,
}: {
  loading?: boolean;
  value?: ResizeImageResult;
  onChange?: (data: ResizeImageResult) => void;
}) {
  return (
    <SignupAvatarRow>
      <ImageInput width={200} height={200} cropping onChange={onChange}>
        <Avatar avatarUrl={value?.url} noOutline center size="md" editable loading={loading} />
      </ImageInput>
      <SignupPictureHint>Please provide a picture</SignupPictureHint>
    </SignupAvatarRow>
  );
}

const KNOWN_PHONE_PREFIXES = ['98', '1'];

function splitPhoneNumber(phoneNumber?: string | null) {
  if (!phoneNumber) {
    return { prefix: undefined as string | undefined, phone: undefined as number | undefined };
  }
  const matchedPrefix = KNOWN_PHONE_PREFIXES.find((prefix) => phoneNumber.startsWith(prefix));
  const rest = matchedPrefix ? phoneNumber.slice(matchedPrefix.length) : phoneNumber;
  const digits = rest.replace(/\D/g, '');
  return {
    prefix: `+${matchedPrefix}`,
    phone: digits ? Number(digits) : undefined,
  };
}

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

function Signup() {
  const history = useHistory();
  const { data: loggedInData } = useLoggedInStateQuery();
  const [loginLoading, onRegisters] = useRegister();
  const [updateUserProfile, { loading: updateProfileLoading }] = useUpdateProfileMutation();
  const [lookupUserByEmail, { data: userLookupData, loading: userLookupLoading }] = useGetUserByEmailLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [updateReference, { loading: updateReferenceLoading }] = useUpdateReferenceMutation();
  const [applicationReceived, setApplicationReceived] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [pendingUserFound, setPendingUserFound] = useState(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [signupForm] = Form.useForm();

  const clearRedirectTimer = () => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (loggedInData?.loggedInState) {
      history.replace('/account');
    }
  }, [history, loggedInData]);

  useEffect(() => () => clearRedirectTimer(), []);

  const normalizeEmail = (value?: string) => (value ?? '').trim().toLowerCase();

  const handleEmailBlur = () => {
    const email = normalizeEmail(signupForm.getFieldValue('email'));
    if (!email || !isValidEmail(email)) {
      return;
    }
    lookupUserByEmail({ variables: { email } });
  };

  // Prefill the form when the entered email belongs to an existing pending application,
  // so the applicant can review their details and update their reference email.
  useEffect(() => {
    const matchedUser = userLookupData?.getUserByEmail?.data;
    const attrs = matchedUser?.attributes;
    if (!attrs || attrs.status !== Enum_Userspermissionsuser_Status.Pending) {
      setPendingUserFound(false);
      return;
    }

    const { prefix, phone } = splitPhoneNumber(String(attrs.phoneNumber));
    signupForm.setFieldsValue({
      firstname: attrs.firstname,
      lastname: attrs.lastname,
      birthdate: attrs.birthdate ? moment(attrs.birthdate) : undefined,
      referenceEmail1: attrs.referenceUser,
      ...(prefix ? { prefix } : {}),
      ...(phone ? { phone } : {}),
      ...(attrs.avatarUrl ? { avatar: { url: attrs.avatarUrl, blob: null } } : {}),
    });
    setPendingUserFound(true);
  }, [userLookupData, signupForm]);

  const onFinish = (values: unknown) => {
    setSignupError(null);
    const { firstname, lastname, email, birthdate, prefix, phone, password, avatar, referenceEmail1 } =
      values as SignupProps;

    // Updating an existing pending application: only the reference email changes.
    if (pendingUserFound) {
      updateReference({
        variables: {
          input: {
            email,
            referenceUser: referenceEmail1,
          },
        },
      })
        .then(() => {
          setApplicationReceived(true);
        })
        .catch((err: unknown) => {
          setSignupError(err instanceof Error ? err.message : 'Failed to update the reference email.');
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        });
      return;
    }

    const registerInput = {
      password,
      email,
      username: email,
      firstname,
      lastname,
      birthdate: birthdate.toISOString(),
      phoneNumber: prefix + phone,
      referenceUser: referenceEmail1,
      ...(avatar?.blob ? { avatar: avatar.blob } : {}),
    };

    onRegisters(
      registerInput,
      async () => {
        await updateUserProfile({
          variables: { input: { registerUser: true } },
        });
        setApplicationReceived(true);
      },
      (errorMessage) => {
        setSignupError(errorMessage);
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      },
    );
  };

  const toLogin = () => {
    clearRedirectTimer();
    history.push('/login');
  };

  const loading = loginLoading || updateProfileLoading || updateReferenceLoading;

  if (applicationReceived) {
    return (
      <AuthPage>
        <AuthPageCard>
          <LoginBody>
            <LogoMark>
              <img src={Logo} alt="" width={56} height={56} />
            </LogoMark>
            <LoginHeading>Application received</LoginHeading>
            <LoginSubheading>Please wait until your reference accepts your application.</LoginSubheading>
          </LoginBody>
        </AuthPageCard>
      </AuthPage>
    );
  }

  return (
    <AuthPage>
      <AuthPageCard>
        <LoginBody>
          <LogoMark>
            <img src={Logo} alt="" width={56} height={56} />
          </LogoMark>
          <LoginHeading>Sign up</LoginHeading>
          <LoginSubheading>
            <span style={{ display: 'block', whiteSpace: 'nowrap' }}>
              Explore Zoroastrian groups and events with Polgozār.
            </span>
            We take your privacy and security seriously. Your personal information is protected with secure technology and is only used to provide you with access to our services. We do not sell, share, or modify your information without your permission.
          </LoginSubheading>
        </LoginBody>
        {signupError ? (
          <Alert
            type="error"
            message={signupError}
            showIcon
            closable
            onClose={() => setSignupError(null)}
            style={{ marginBottom: 20 }}
          />
        ) : null}
        {pendingUserFound ? (
          <Alert
            type="info"
            message="We found your pending application. Your details are prefilled below — update your reference email and submit again."
            showIcon
            style={{ marginBottom: 20 }}
          />
        ) : null}
        <StyledForm layout="vertical" form={signupForm} onFinish={onFinish} preserve={false}>
          <Form.Item name="avatar" style={{ marginBottom: 20, textAlign: 'center' }}>
            <AvatarInput loading={updateProfileLoading} />
          </Form.Item>
          <Row gutter={[16, 0]}>
            <Col xs={12} md={12}>
              <Form.Item
                name="firstname"
                label="First name"
                rules={[{ required: true, message: 'Please enter your first name.' }]}
              >
                <Input className="auth-input-text" placeholder="First name" autoComplete="given-name" />
              </Form.Item>
            </Col>
            <Col xs={12} md={12}>
              <Form.Item
                name="lastname"
                label="Last name"
                rules={[{ required: true, message: 'Please enter your last name.' }]}
              >
                <Input className="auth-input-text" placeholder="Last name" autoComplete="family-name" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="birthdate"
                label="Birthdate"
                rules={[{ required: true, message: 'Please enter your birthdate.' }]}
              >
                <DatePicker
                  className="auth-date-input"
                  format={birthDayFormat}
                  placeholder={birthDayFormat}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="phone" label="Phone number">
                <PhoneInput className="auth-phone-combo" controls={false} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { type: 'email', message: 'The input is not a valid email.' },
                  { required: true, message: 'Please enter your email.' },
                ]}
              >
                <Input
                  className="auth-input-text"
                  placeholder="Enter your email"
                  autoComplete="email"
                  onBlur={handleEmailBlur}
                  suffix={userLookupLoading ? <Spin size="small" /> : <span />}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="referenceEmail1"
                label="Reference email"
                rules={[
                  { type: 'email', message: 'The input is not a valid email.' },
                  { required: true, message: 'Please enter your reference email.' },
                ]}
              >
                <Input className="auth-input-text" placeholder="Reference" autoComplete="off" />
              </Form.Item>
            </Col>
          </Row>
          <SignupRecordingNotice>
            Please note that meetings may be recorded at the discretion of the organizer.
          </SignupRecordingNotice>
          <SignupRiskAcknowledgement>
            <Form.Item
              name="riskAcknowledged"
              valuePropName="checked"
              initialValue={false}
              rules={[
                {
                  validator: (_rule, value) =>
                    value ? Promise.resolve() : Promise.reject(new Error('Please acknowledge Section 6 to continue.')),
                },
              ]}
            >
              <Checkbox>
                <SignupRiskAcknowledgementText>
                  I have read and acknowledge{' '}
                  <Link to="/terms#assumption-of-risk" target="_blank" rel="noopener noreferrer">
                    Section 6 (Assumption of Risk) of the Terms of Service
                  </Link>
                  , including the risks of being identified as Zoroastrian and Polgozar&apos;s limits on liability for
                  third-party or foreign government action.
                </SignupRiskAcknowledgementText>
              </Checkbox>
            </Form.Item>
          </SignupRiskAcknowledgement>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: 'Please enter your password.' }]}
                hasFeedback
              >
                <Input.Password
                  className="login-input-password"
                  placeholder="Enter your password"
                  autoComplete="new-password"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="confirm"
                label="Confirm password"
                dependencies={['password']}
                hasFeedback
                rules={[
                  { required: true, message: 'Please confirm your password.' },
                  ({ getFieldValue }) => ({
                    validator(_: unknown, value: string) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('The two passwords do not match.'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  className="login-input-password"
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0 }}>
            <SignInButton htmlType="submit" loading={loading}>
              {pendingUserFound ? 'Submit' : 'Sign up'}
            </SignInButton>
          </Form.Item>
        </StyledForm>
        <LoginFooter>
          Already have an account?{' '}
          <SignUpLink type="button" onClick={toLogin}>
            Sign in
          </SignUpLink>
        </LoginFooter>
      </AuthPageCard>
    </AuthPage>
  );
}

export default Signup;
